import styled from '@emotion/styled';
import { spacer, color } from '@tokens';
import { Icon } from '@components/Icon/Icon';
import { Text } from '@components/Text/Text';
import { useStrings } from '@hooks/useStrings';
import { useInterpolate } from '@hooks/useInterpolate';

export type MultiSelectValue = string[];

export interface MultiSelectProps {
  /**
   * Search term that was used by the filter/search component
   */
  searchTerm?: string;
}

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: ${spacer.x1} ${spacer.x2};
`;

const StyledIcon = styled(Icon)`
  color: ${color.text_disabled};
  margin-bottom: ${spacer.x1};
`;

const StyledText = styled(Text)`
  text-align: center;
`;

/**
 * EmptyState for the MultiSelect component.
 *
 * {@link https://zeroheight.com/76c439891/p/79ef8f-multiselect-input/t/42c130 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-multiselect Code documentation}
 *
 * @example
 * const searchTerm = 'fwew';
 * return <EmptyState searchTerm={tags} onChange={setTags} />;
 */
export const EmptyState = ({ searchTerm }: MultiSelectProps): JSX.Element => {
  const { multiSelect } = useStrings();
  const interpolate = useInterpolate();

  return (
    <StyledContainer>
      <StyledIcon icon="tray_empty" size="l"></StyledIcon>
      {searchTerm ? (
        <StyledText>
          {interpolate(multiSelect.noSearchTermMatch, { searchTerm })}
        </StyledText>
      ) : (
        <StyledText>{multiSelect.emptyState}</StyledText>
      )}
    </StyledContainer>
  );
};
